/**
 * Summary cards for Portfolio Cost & Savings.
 * Semantic color only on NPV, cost per ton, cost per capita (favorable/unfavorable). Others neutral.
 */
import React from 'react';
import { currencyShort, getFinancialColor, FINANCIAL_COLORS, TOOLTIP_NEGATIVE_SAVINGS } from './portfolioCostUtils';

export interface PortfolioMetrics {
  upfrontCost: number;
  npv: number;
  costPerTon: number | null;
  paybackYear: number | null;
  fuelSavings: number;
  emissionsReductionMT: number;
  costPerCapita: number | null;
}

const cardStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  background: '#fff',
  minWidth: 0,
};
const labelStyle: React.CSSProperties = { fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 };

function Card({
  label,
  value,
  title,
  valueColor,
}: {
  label: string;
  value: string | number;
  title?: string;
  valueColor?: string;
}) {
  return (
    <div style={cardStyle} title={title}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: valueColor ?? FINANCIAL_COLORS.neutral }}>{value}</div>
    </div>
  );
}

export interface PortfolioCostSummaryCardsProps {
  metrics: PortfolioMetrics;
  /** Optional: show per-capita only when population is set */
  population?: number | null;
}

export const PortfolioCostSummaryCards: React.FC<PortfolioCostSummaryCardsProps> = ({
  metrics,
  population,
}) => {
  const costPerCapita =
    population != null && population > 0 && metrics.costPerCapita != null
      ? metrics.costPerCapita
      : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
      <Card
        label="Upfront cost"
        value={currencyShort(metrics.upfrontCost)}
        title="Total capital (buildings + transport)."
      />
      <Card
        label="NPV"
        value={currencyShort(metrics.npv)}
        title={`Net present value of (savings − costs). ${metrics.npv < 0 ? TOOLTIP_NEGATIVE_SAVINGS : 'Positive = net benefit.'}`}
        valueColor={getFinancialColor(metrics.npv, 'npv')}
      />
      <Card
        label="Cost per ton"
        value={metrics.costPerTon != null ? currencyShort(metrics.costPerTon) : '—'}
        title={metrics.costPerTon != null && metrics.costPerTon < 0 ? `Cumulative net cost ÷ emissions reduced. ${TOOLTIP_NEGATIVE_SAVINGS}` : 'Cumulative net cost ÷ emissions reduced.'}
        valueColor={metrics.costPerTon != null ? getFinancialColor(metrics.costPerTon, 'cost_per_ton') : undefined}
      />
      <Card
        label="Payback year"
        value={metrics.paybackYear != null ? String(metrics.paybackYear) : 'Never'}
        title="First year when cumulative net cost ≤ 0. Only shown if portfolio pays back by 2045."
        valueColor={metrics.paybackYear == null ? FINANCIAL_COLORS.muted : undefined}
      />
      <Card
        label="Fuel savings"
        value={currencyShort(metrics.fuelSavings)}
        title="Cumulative fuel savings (transport) + operating savings (buildings) over 2023–2045."
      />
      <Card
        label="Emissions reduction (tons)"
        value={metrics.emissionsReductionMT.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        title="Total MT CO₂e reduced (buildings + transport) over analysis period."
      />
      {costPerCapita != null && (
        <Card
          label="Cost per capita"
          value={currencyShort(costPerCapita)}
          title={`Cumulative net cost ÷ population. ${costPerCapita < 0 ? TOOLTIP_NEGATIVE_SAVINGS : ''}`}
          valueColor={getFinancialColor(costPerCapita, 'cost_per_capita')}
        />
      )}
    </div>
  );
};
